<template>
  <div>
    <Modal
      ref="newPostModal"
      v-model="showModal"
      :mask="false"
      :width="modalWidth"
      title="Create a New Post"
      draggable
      scrollable
      :closable="false"
      :fullscreen="fullscreen"
      class="post-modal"
      @on-visible-change="onModalVisibleChange"
    >
      <Spin
        v-if="modalLoading"
        fix
        size="large"
      >
        <i
          class="fas fa-spinner fa-spin"
          style="font-size: 40px; color: var(--color-green-thick);"
        />
        <p style="color: var(--color-green-thick)">
          Submitting...
        </p>
      </Spin>
      <div
        slot="header"
        class="modal-header"
      >
        <div class="ivu-modal-header-inner">
          <span>Create a New Post</span>
          <span v-if="isFeedback"> (Feedback)</span>
        </div>
        <Tooltip
          v-if="fullscreen"
          content="Close Full Screen"
          placement="top"
        >
          <Icon
            type="ios-contract"
            class="header-icon"
            @click="onCloseModalFullScreen"
          />
        </Tooltip>
        <Tooltip
          v-else
          content="Open Full Screen"
          placement="top"
        >
          <Icon
            type="ios-expand"
            class="header-icon"
            @click="onShowModalFullScreen"
          />
        </Tooltip>
        <Tooltip
          content="Close"
          placement="top"
        >
          <Icon
            type="ios-close"
            class="header-icon"
            style="font-size: 32px;"
            @click="onModalVisibleChange(false)"
          />
        </Tooltip>
      </div>
      <Form
        ref="newPost"
        :model="newPost"
        :rules="ruleValidate"
        class="post-form"
      >
        <FormItem
          :required="true"
          prop="title"
          :style="{ '--posts-visibility': relativePosts.length ? 'visible' : 'hidden' }"
          class="post-title"
        >
          <Poptip
            v-model="recommendationVisible"
            trigger="focus"
            style="width: 100%;"
          >
            <Input
              v-model="newPost.title"
              placeholder="Title"
              clearable
            />
            <div
              slot="content"
              class="post-poptip"
            >
              <div class="filter-instruction-wrapper">
                <p class="filter-instruction">
                  Your question may already have answers:
                </p>
                <Icon
                  type="ios-close"
                  class="filter-close-icon"
                  @click="recommendationVisible=false"
                />
              </div>
              <div
                v-for="(post, index) in relativePosts"
                :key="`${post.item.id}-${index}`"
                class="post-item"
              >
                <a
                  :ref="`post-link-${index}`"
                  v-dompurify-html="highLightedPost(post)"
                  :href="postLink(post.item.id)"
                  target="_blank"
                  class="post-link"
                />
                <div class="meta-data-wrapper">
                  <span class="meta-data">{{ `${users.find(user => user.id === post.item.authorId).name} - ${formattedCreatedAt(post.item.createdAt)}` }}</span>
                </div>
              </div>
            </div>
          </Poptip>
        </FormItem>
        <FormItem>
          <Select
            v-model="tagsId"
            placeholder="Tag"
            multiple
            filterable
            clearable
            class="post-tag-list"
          >
            <OptionGroup
              v-for="group in forumTags"
              :key="group.label"
              :label="group.label"
            >
              <Option
                v-for="item in group.value"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </Option>
            </OptionGroup>
          </Select>
        </FormItem>
        <FormItem
          :required="true"
          prop="content"
        >
          <div
            class="text-editor"
            :style="{'--editor-min-height': fullscreen ? '500px' : '200px'}"
          >
            <ContentEditor
              ref="contentEditor"
              v-model="newPostContent"
              :default-content-html="defaultContentHtml"
              editable
              is-post
            />
          </div>
        </FormItem>
      </Form>
      <div v-if="isFeedback">
        <Checkbox
          v-model="sendEmail"
          class="email-sync-wrapper"
        >
          Send email to owner(s):
        </Checkbox>
        <div class="owner-email-list">
          <Tag
            v-for="email in receiverEmail"
            :key="email"
            color="green"
            class="owner-email"
          >
            {{ email }}
          </Tag>
        </div>
      </div>

      <div slot="footer">
        <div class="modal-buttons">
          <Button
            type="success"
            @click="submitPost"
          >
            Submit
          </Button>
          <Button
            type="default"
            class="button"
            @click="onModalVisibleChange(false)"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import ContentEditor from './ContentEditor'
import { saveAndReplaceImagesWithinPost, isContentEmpty } from '@/helpers/forum'
import _ from 'lodash'
import axios from 'axios'
import { forumTagsConstant } from '@/constants/forum-tags.js'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { fuzzySearch } from '@/helpers/fuzzy-search'
import UserMixin from '@/mixins/UserMixin'

const DEFAULT_EDITOR_CONTENT = {
  JSON: {
    content: {}
  },
  HTML: '',
  IMAGES: null,
  ATTACHMENTS: null
}

const DEFAULT_NEW_POST = {
  title: '',
  categoryId: null,
  content: {},
  authorId: null,
  authorName: '',
  attachments: []
}

export default {
  components: {
    ContentEditor
  },

  mixins: [
    UserMixin
  ],

  model: {
    prop: 'showModal',
    event: 'change'
  },

  props: {
    showModal: {
      type: Boolean,
      default: true
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    defaultContentHtml: {
      type: String,
      default: ''
    }
  },

  data () {
    const validateContent = (rule, value, callback) => {
      if (isContentEmpty(this.newPostContent.HTML)) {
        callback(new Error('Please enter the content.'))
      } else {
        callback()
      }
    }

    return {
      author: null,
      newPost: _.cloneDeep(DEFAULT_NEW_POST),
      tagsId: [],
      newPostContent: _.cloneDeep(DEFAULT_EDITOR_CONTENT),
      ruleValidate: {
        title: [
          { required: true, message: 'Title is required', trigger: 'blur' }
        ],
        content: [
          { validator: validateContent, trigger: 'blur' }
        ]
      },
      newPostPromise: undefined,
      forumTags: forumTagsConstant,
      relativePosts: [],
      recommendationVisible: false,
      filterNumber: 5,
      receiverEmail: '',
      sendEmail: true,
      modalLoading: false,
      isFeedback: false
    }
  },

  computed: {
    modalWidth () {
      return this.fullscreen ? '100%' : `${window.innerWidth * 0.5}px`
    },

    embeddedImages () {
      return this.newPostContent.IMAGES || []
    },

    ...mapGetters('forum', {
      posts: 'postsArr'
    })
  },

  watch: {
    newPostContent: {
      handler () {
        this.newPost.content = this.newPostContent.JSON
        this.newPost.attachments = this.newPostContent.ATTACHMENTS
        this.newPost.markup = this.newPostContent.HTML
      },
      deep: true
    },

    async 'newPost.title' () {
      if (!this.posts.length) {
        await this.fetchPosts()
      }

      // Fuzzy search of posts based on posts (reply) title & content
      const options = {
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: this.newPost.title.length / 2,
        threshold: 0.6,
        distance: 100
      }
      const keys = ['title']
      this.relativePosts = fuzzySearch(this.posts, this.newPost.title, keys, { ...options })
        .filter(post => post.score <= 0.5)
        .sort((a, b) => a.score < b.score ? -1 : 1)
        .slice(0, this.filterNumber)
    },

    '$route.query.feedback' (newVal) {
      this.isFeedback = newVal || false
    }
  },

  created () {
    this.author = this.$auth.getUser()
    this.fetchAllUsers()
    this.setupFeedbackData()
  },

  methods: {
    // TODO: save post data to localStorage
    updateAuthorId () {
      this.newPost.authorId = this.author.id
      this.newPost.authorName = this.author.name
    },

    /**
     * Funcion triggered to submit new post to db
     */
    async submitPost () {
      this.updateAuthorId()
      // Form validation
      this.$refs.newPost.validate(async valid => {
        if (valid) {
          this.modalLoading = true
          // Send email to issue onwers if this is a feedback
          try {
            const title = this.newPost.title
            const content = this.newPostContent.HTML

            if (this.embeddedImages.length) {
              this.newPost.content.content = await saveAndReplaceImagesWithinPost(this.newPost.content.content, this.embeddedImages)
            }

            this.$log('forum_post', this.newPost.title, this.newPost)
            this.newPostPromise = await this.$store.dispatch('forum/addPost', {
              post: this.newPost,
              tagsId: this.tagsId
            })

            if (this.isFeedback && this.sendEmail) {
              try {
                await axios.post(`${process.env.VUE_APP_API_URI}/emails`, {
                  from: 'do-not-reply@li.me',
                  to: this.receiverEmail.concat([this.$auth.getUser().email]),
                  subject: title,
                  content: content,
                  contentType: 'html',
                  forumPostId: this.newPostPromise.data
                })
              } catch (error) {
                this.$Message.error(`Fail to send the feedback email to ${this.receiverEmail}`)
                console.error(`Error while sending feedback email to ${this.receiverEmail}: ${error.message}`)
              }
            }

            this.modalLoading = false
          } catch (error) {
            this.modalLoading = false
            this.$Message.error(`Fail to submit new post.`)
            this.$Message.error(`Error while submitting post: ${error.message}`)
          }

          this.$Message.info('Post form submitted!')
          this.$router.push({
            name: 'Forum Post',
            params: {
              id: this.newPostPromise.data,
              users: this.users
            }
          })

          this.newPostContent = {}
          this.newPost = _.cloneDeep(DEFAULT_NEW_POST)
          this.$refs.contentEditor.editor.clearContent()
          this.onModalVisibleChange(false)
        } else {
          this.$Message.error('New post content is not valid.')
        }
      })
    },

    /**
     * Enable the fullscreen mode for new post modal
     * Listen to event from NewPost component
     */
    onShowModalFullScreen () {
      this.$emit('show-fullscreen', true)
      const query = { ...this.$route.query, 'newpost': true, 'fullscreen': true }
      this.$router.replace({
        ...this.$router,
        query
      })
    },

    /**
     * Disable the fullscreen mode for new post modal
     * Listen to event from NewPost component
     */
    onCloseModalFullScreen () {
      this.$emit('show-fullscreen', false)
      const query = { ...this.$route.query, fullscreen: undefined }
      this.$router.replace({
        ...this.$router,
        query
      })
    },

    /**
     * Close the new post creation modal
     * Listen to event passed by NewPost component
     */
    onCloseModal () {
      const query = { ...this.$route.query, newpost: undefined, fullscreen: undefined, title: undefined, receiverEmail: undefined, tagName: undefined, targetUrl: undefined, feedback: undefined }
      this.$router.replace({
        ...this.$router,
        query
      })
      this.resetForm()
    },

    onModalVisibleChange (isVisible) {
      if (!isVisible) {
        this.onCloseModal()
      }
      this.$emit('change', isVisible)
    },

    /**
     * Reset the form data for new post after close the modal
     * Triggered whenever the new post modal visibility changes
     */
    resetForm () {
      this.newPost = _.cloneDeep(DEFAULT_NEW_POST)
      this.newPostContent = _.cloneDeep(DEFAULT_EDITOR_CONTENT)
      this.tagsId = []
      this.$refs['newPost'].resetFields()
    },

    /**
     * Get link href for the recommendated relative post
     * @param {Number} postId
     * @return {String} href
     */
    postLink (postId) {
      return `${process.env.VUE_APP_APP_URI}/forum/post/${postId}`
    },

    /**
     * Construct the highlighted html for embedding
     * @param {Object} postItem
     * @return {String}
     */
    highLightedPost (postItem) {
      if (!postItem.matches.length) {
        return postItem.item.title
      }
      const originTitle = postItem.item.title
      let previousPosition = 0
      let updatedTitle = ''
      postItem.matches[0].indices.forEach(index => {
        updatedTitle += originTitle.slice(previousPosition, index[0]) +
        '<span class="title-highlight">' + originTitle.slice(index[0], index[1] + 1) + '</span>'
        previousPosition = index[1] + 1
      })
      updatedTitle += originTitle.slice(previousPosition, originTitle.length)
      return updatedTitle
    },

    /**
     * Fetch posts for fuzzy search
     */
    async fetchPosts () {
      this.loadingPosts = true
      if (!this.posts.length) {
        await this.$store.dispatch('forum/fetchPosts')
      }
      this.loadingPosts = false
    },

    /**
     * Get formatted date of post creation date
     */
    formattedCreatedAt (time) {
      return moment(time).format('MMM Do, YYYY')
    },

    /**
     * Check on the url to see if this origins from a feedback source
     * If so, populate the meta data given the url
     */
    setupFeedbackData () {
      const emails = this.$route.query.receiverEmail
      this.receiverEmail = emails ? (Array.isArray(emails) ? emails : [emails]) : null
      if (this.receiverEmail) {
        this.isFeedback = true
        this.populateMetaData()
      }
    },

    /**
     * Populate the title and tags from URL
     * Data may originat from feedback source
     */
    populateMetaData () {
      const { title, tagName } = this.$route.query
      this.$set(this.newPost, 'title', `Feedback: ${title}`)
      const tagsList = this.forumTags.reduce((acc, cur) => {
        return [...acc, ...cur.value]
      }, [])
      const tag = tagsList.find(item => item.name === tagName)

      if (tag && tag.id) {
        this.tagsId = [tag.id]
      }
    }
  }
}
</script>

<style scoped>
.post-form {
  padding: 7px;
}

.text-editor {
  color: var(--color-text-content);
}

.text-editor >>> .ProseMirror {
  min-height: var(--editor-min-height);
}

.post-tag-list >>> .ivu-select-group-title {
  font-weight: bold;
  color: var(--color-gray-dark);
}

.modal-header {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.modal-header >>> .ivu-modal-header-inner {
  width: 98%;
}

.header-icon {
  font-size: 16px;
  cursor: pointer;
}

.post-title >>> .ivu-poptip-rel{
  display: inline;
}

.post-title >>> .ivu-poptip-popper {
  width: 100%;
}

.post-link {
  color: var(--color-text-content);
}

.post-item {
  padding: 5px;
}

.post-item:hover {
  background: var(--color-gray-hover);
}

.post-title >>> .ivu-poptip-popper{
  visibility: var(--posts-visibility);
}

.post-link >>> .title-highlight {
  background-color: var(--color-yellow-light)
}

.filter-instruction-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.filter-instruction {
  font-weight: bold;
  font-size: 14px;
}

.filter-close-icon {
  font-size: 20px;
  cursor: pointer;
}

.meta-data-wrapper {
  color: var(--color-text-light);
  font-style: italic;
  font-size: 12px;
}

.meta-data {
  margin-left: 20px;
}

.email-sync-wrapper {
  margin-left: 5px;
}

.owner-email-list {
  font-size: 12px;
  padding: 2px;
}
</style>
